import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { api, formatDate, formatApiErrorDetail, mediaUrl } from "../lib/api";
import RichEditor from "./RichEditor";
import MediaPicker from "./MediaPicker";

const EMPTY = {
  title: "", slug: "", excerpt: "", content: "", cover_id: null, cover_alt: "",
  category: "", tags: [], post_type: "Artikel", featured: false, sources: [],
  update_notes: "", seo_title: "", seo_description: "", status: "draft", scheduled_at: null,
};

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState(isNew ? { ...EMPTY } : null);
  const [categories, setCategories] = useState([]);
  const [postTypes, setPostTypes] = useState([]);
  const [saveState, setSaveState] = useState("idle"); // idle | dirty | saving | saved | error
  const [error, setError] = useState("");
  const [revisions, setRevisions] = useState([]);
  const [coverPicker, setCoverPicker] = useState(false);
  const [scheduleValue, setScheduleValue] = useState("");
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      setCategories(r.data.categories || []);
      setPostTypes(r.data.post_types || ["Artikel"]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/admin/articles/${id}`)
      .then((r) => {
        setForm({ ...EMPTY, ...r.data });
        loadRevisions();
      })
      .catch(() => setError("Tulisan tidak ditemukan"));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRevisions = () => {
    api.get(`/admin/articles/${id}/revisions`).then((r) => setRevisions(r.data.items)).catch(() => {});
  };

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    dirtyRef.current = true;
    setSaveState("dirty");
  };

  const save = async (overrides = {}) => {
    if (!form) return null;
    const payload = {
      title: form.title, slug: form.slug || form.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, ""),
      excerpt: form.excerpt, content: form.content, cover_id: form.cover_id, cover_alt: form.cover_alt,
      category: form.category, tags: Array.isArray(form.tags) ? form.tags : String(form.tags).split(",").map((t) => t.trim()).filter(Boolean),
      post_type: form.post_type, featured: form.featured, sources: form.sources,
      update_notes: form.update_notes, seo_title: form.seo_title, seo_description: form.seo_description,
      status: overrides.status || form.status,
      scheduled_at: overrides.scheduled_at !== undefined ? overrides.scheduled_at : (form.scheduled_at || null),
    };
    try {
      if (isNew) {
        const { data } = await api.post("/admin/articles", payload);
        setSaveState("saved");
        navigate(`/admin/tulisan/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/admin/articles/${id}`, payload);
        setSaveState("saved");
        return data;
      }
    } catch (err) {
      setSaveState("error");
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      throw err;
    }
  };

  // Autosave
  useEffect(() => {
    if (saveState !== "dirty" || !form || !form.title.trim()) return;
    timerRef.current = setTimeout(() => {
      setSaveState("saving");
      save().catch(() => {});
    }, 2500);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveState, form]);

  const doAction = async (status) => {
    setError("");
    setSaveState("saving");
    try {
      await save({ status, ...(status === "scheduled" ? { scheduled_at: scheduleValue ? new Date(scheduleValue).toISOString() : null } : {}) });
      if (status === "published" || status === "draft" || status === "trashed") {
        await api.get(`/admin/articles/${id || ""}`);
        if (status === "trashed") navigate("/admin/tulisan");
      }
    } catch (e) {}
  };

  const restoreRevision = async (revId) => {
    try {
      await api.post(`/admin/articles/${id}/revisions/${revId}/restore`);
      const r = await api.get(`/admin/articles/${id}`);
      setForm({ ...EMPTY, ...r.data });
      setSaveState("saved");
      loadRevisions();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!form) {
    return error ? (
      <div className="text-center py-20">
        <p className="text-enji">{error}</p>
        <Link to="/admin/tulisan" className="btn-ghost mt-6">Kembali ke daftar</Link>
      </div>
    ) : (
      <p className="text-sub text-sm">Memuat…</p>
    );
  }

  const saveLabel = { idle: "", dirty: "Belum tersimpan", saving: "Menyimpan…", saved: "Tersimpan", error: "Gagal menyimpan" };

  return (
    <div data-testid="article-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/admin/tulisan" className="text-[13px] text-sub hover:text-ink">&larr; Semua tulisan</Link>
          <h1 className="font-serif text-3xl text-ink mt-2">{isNew ? "Tulis Artikel Baru" : "Sunting Tulisan"}</h1>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="article-save-indicator" className={`text-[13px] font-semibold ${saveState === "error" ? "text-enji" : saveState === "saved" ? "text-moss" : "text-faint"}`}>
            {saveLabel[saveState]}
          </span>
          {!isNew && form.status === "published" && (
            <Link to={`/tulisan/${form.slug}`} target="_blank" data-testid="article-view-live" className="btn-ghost !min-h-[40px] !px-4 text-sm">Lihat di situs</Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        <div className="lg:col-span-2 space-y-5">
          <div>
            <label className="field-label" htmlFor="ae-title">Judul *</label>
            <input id="ae-title" data-testid="article-title-input" required value={form.title} onChange={(e) => set("title", e.target.value)} className="field-input !text-lg" placeholder="Judul tulisan" />
          </div>
          <div>
            <label className="field-label" htmlFor="ae-excerpt">Ringkasan pembuka</label>
            <textarea id="ae-excerpt" data-testid="article-excerpt-input" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className="field-input !min-h-[90px]" placeholder="Satu-dua kalimat yang muncul di kartu dan bagian atas artikel" />
          </div>
          <div>
            <label className="field-label">Isi tulisan</label>
            <RichEditor value={form.content} onChange={(html) => set("content", html)} placeholder="Tulis isi di sini…" />
          </div>

          <div className="bg-surface border border-line rounded-lg p-5">
            <p className="font-semibold text-ink text-sm mb-3">Sumber / Referensi</p>
            {(form.sources || []).map((s, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input value={s.label || ""} onChange={(e) => { const arr = [...form.sources]; arr[i] = { ...arr[i], label: e.target.value }; set("sources", arr); }} placeholder="Label" className="field-input !min-h-[40px]" data-testid={`source-label-${i}`} />
                <input value={s.url || ""} onChange={(e) => { const arr = [...form.sources]; arr[i] = { ...arr[i], url: e.target.value }; set("sources", arr); }} placeholder="https://URL" className="field-input !min-h-[40px]" data-testid={`source-url-${i}`} />
                <button type="button" aria-label="Hapus sumber" onClick={() => set("sources", form.sources.filter((_, j) => j !== i))} className="w-10 h-10 rounded-md border border-line text-sub hover:text-enji shrink-0"><Trash2 size={15} className="mx-auto" /></button>
              </div>
            ))}
            <button type="button" data-testid="add-source-button" onClick={() => set("sources", [...(form.sources || []), { label: "", url: "" }])} className="btn-ghost !min-h-[40px] !px-4 text-sm"><Plus size={14} /> Tambah sumber</button>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-surface border border-line rounded-lg p-5 space-y-4">
            <p className="font-semibold text-ink text-sm">Publikasi</p>
            <p className="text-[13px] text-faint">Status: <span className="font-semibold text-ink">{form.status}</span></p>
            <div className="flex flex-wrap gap-2">
              <button type="button" data-testid="article-save-draft" onClick={() => (isNew ? save({ status: "draft" }).catch(() => {}) : doAction("draft"))} className="btn-ghost !min-h-[40px] !px-4 text-sm">Simpan Draf</button>
              <button type="button" data-testid="article-publish-button" onClick={() => (isNew ? save({ status: "published" }).catch(() => {}) : doAction("published"))} className="btn-primary !min-h-[40px] !px-4 text-sm">Terbitkan</button>
              {!isNew && form.status === "published" && (
                <button type="button" data-testid="article-unpublish-button" onClick={() => doAction("draft")} className="btn-ghost !min-h-[40px] !px-4 text-sm">Turunkan ke Draf</button>
              )}
              {!isNew && (
                <button type="button" data-testid="article-trash-button" onClick={() => { if (window.confirm("Pindahkan ke tong sampah?")) doAction("trashed"); }} className="btn-ghost !min-h-[40px] !px-4 text-sm !text-enji">Tong Sampah</button>
              )}
            </div>
            <div>
              <label className="field-label" htmlFor="ae-schedule">Jadwalkan terbit (WITA)</label>
              <input id="ae-schedule" data-testid="article-schedule-input" type="datetime-local" value={scheduleValue} onChange={(e) => setScheduleValue(e.target.value)} className="field-input !min-h-[40px]" />
              <button type="button" data-testid="article-schedule-button" disabled={!scheduleValue || isNew} onClick={() => doAction("scheduled")} className="btn-ghost !min-h-[40px] !px-4 text-sm mt-2 w-full">Jadwalkan</button>
              {!isNew && form.scheduled_at && <p className="text-[12px] text-faint mt-2">Terjadwal: {formatDate(form.scheduled_at)}</p>}
            </div>
            {error && <p role="alert" className="text-[13px] text-enji">{error}</p>}
          </div>

          <div className="bg-surface border border-line rounded-lg p-5 space-y-4">
            <p className="font-semibold text-ink text-sm">Meta</p>
            <div>
              <label className="field-label" htmlFor="ae-slug">Slug</label>
              <input id="ae-slug" data-testid="article-slug-input" value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-"))} className="field-input !min-h-[40px]" placeholder="otomatis-dari-judul" />
              <p className="text-[12px] text-faint mt-1">Mengubah slug tulisan yang sudah terbit memasang pengalihan otomatis dari tautan lama.</p>
            </div>
            <div>
              <label className="field-label" htmlFor="ae-category">Kategori</label>
              <select id="ae-category" data-testid="article-category-select" value={form.category} onChange={(e) => set("category", e.target.value)} className="field-input !min-h-[40px]">
                <option value="">— Pilih kategori —</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="ae-type">Jenis tulisan</label>
              <select id="ae-type" data-testid="article-type-select" value={form.post_type} onChange={(e) => set("post_type", e.target.value)} className="field-input !min-h-[40px]">
                {postTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="ae-tags">Tag (pisahkan dengan koma)</label>
              <input id="ae-tags" data-testid="article-tags-input" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => set("tags", e.target.value)} className="field-input !min-h-[40px]" placeholder="jepang, beasiswa" />
            </div>
            <label className="flex items-center gap-2.5 text-sm text-ink cursor-pointer">
              <input type="checkbox" data-testid="article-featured-checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[var(--enji)]" />
              Jadikan tulisan unggulan (tampil di beranda)
            </label>
          </div>

          <div className="bg-surface border border-line rounded-lg p-5">
            <p className="font-semibold text-ink text-sm mb-3">Cover (opsional)</p>
            {form.cover_id ? (
              <div className="relative rounded-md overflow-hidden border border-line" style={{ aspectRatio: "3/2" }}>
                <img src={mediaUrl(form.cover_id)} alt={form.cover_alt} className="w-full h-full object-cover" />
                <button type="button" data-testid="article-cover-remove" onClick={() => set("cover_id", null)} aria-label="Hapus cover" className="absolute top-2 right-2 w-8 h-8 rounded-full bg-ivory/90 text-enji flex items-center justify-center">×</button>
              </div>
            ) : (
              <button type="button" data-testid="article-cover-pick" onClick={() => setCoverPicker(true)} className="btn-ghost !min-h-[40px] w-full text-sm">Pilih / unggah cover</button>
            )}
            <input data-testid="article-cover-alt" value={form.cover_alt} onChange={(e) => set("cover_alt", e.target.value)} placeholder="Alt text cover (deskripsi gambar)" className="field-input !min-h-[40px] mt-3 text-sm" />
          </div>

          {!isNew && (
            <div className="bg-surface border border-line rounded-lg p-5">
              <p className="font-semibold text-ink text-sm mb-3">Riwayat Revisi</p>
              {revisions.length === 0 ? (
                <p className="text-[13px] text-faint">Belum ada revisi tersimpan.</p>
              ) : (
                <ul className="space-y-2">
                  {revisions.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-2 text-[13px]">
                      <span className="text-sub truncate">{r.title} · {formatDate(r.saved_at)}</span>
                      <button type="button" data-testid={`revision-restore-${r.id}`} onClick={() => restoreRevision(r.id)} className="text-enji font-semibold whitespace-nowrap">Pulihkan</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      <MediaPicker open={coverPicker} onClose={() => setCoverPicker(false)} onPick={(m) => { set("cover_id", m.id); if (!form.cover_alt) set("cover_alt", m.alt || ""); setCoverPicker(false); }} />
    </div>
  );
}
